import { EJStatus } from 'shared'
import WorkflowRollbackModal from '../../../../../../../../../../../components/CIMSModal/WorkflowRollbackModal'
import { useEJLogContext } from '../../../../EJLogContext'
import CommonEJFormItems from '../CommonEJFormItems'
import { CommonEJFormItemsValues } from '../CommonEJFormItems/types'
import { useForm } from 'antd/lib/form/Form'
import { toMoment } from '../../../../../../../../../../../utils/toMoment'
import { useEJReportsSelectOptionsContext } from '../../../../../EJReportsSelectOptionsContext'
import { useEffect, useState } from 'react'
import { useRevertEngineeringJudgementMutation } from '../../../../../../../../../../../schema'
import { DefaultMutationHookOptions } from '../../../../../../../../../../../utils/defaults'
import { simpleRevertNotification } from '../../../../../../../../../../../hooks/notificationHandler'
import { EJReportOption } from '../../../../../EJReportsSelectOptionsContext/types'
import { errorHandler } from 'hooks/errorHandler'
import { dateFormatter } from '../../../../../../../../../../../utils/dateFormatter'

const RevertEJModal: React.FC = () => {
  const {
    refetch,
    revertModal: { state, toggle, visible },
    metadata: { ejTypes },
    typeOptions,
  } = useEJLogContext()
  const {
    toLabeledValues: toLabeledReports,
    fromLabeledValues: fromLabeledReports,
  } = useEJReportsSelectOptionsContext()
  const [loading, setLoading] = useState<boolean>(false)
  const [form] = useForm<CommonEJFormItemsValues>()
  const [revert] = useRevertEngineeringJudgementMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      revertEngineeringJudgement: {
        judgement: { formattedNumber },
      },
    }) =>
      simpleRevertNotification({
        name: formattedNumber,
        status: EJStatus.Draft.name,
      }),
  })

  useEffect(() => {
    form.setFieldsValue({
      typeId: state?.typeId,
      failureDate: toMoment(state?.failureDate),
      failureDescription: state?.failureDescription,
      justification: state?.justification,
      notes: state?.notes,
      manualReportId: state?.manualReportId,
      reports: toLabeledReports(
        state?.ejToReports?.reduce(
          (
            options,
            {
              concreteTest,
              concretePlacement,
              lot,
              dailyInspectionControlPoint,
              dailyInspectionInspection,
            }
          ) => {
            const report =
              concreteTest ??
              concretePlacement ??
              lot ??
              dailyInspectionControlPoint ??
              dailyInspectionInspection
            if (report) options.push(report)
            return options
          },
          Array<EJReportOption>()
        )
      ),
    })
  }, [form, state, toLabeledReports])

  return (
    <WorkflowRollbackModal
      visible={visible}
      loading={loading}
      onClose={() => toggle(null)}
      endStatus={EJStatus.Draft.name}
      recordName={state?.formattedNumber}
      externalForm={form}
      formItems={
        <CommonEJFormItems
          form={form}
          types={ejTypes}
          typeOptions={typeOptions}
          {...state}
        />
      }
      initialFormValues={{
        typeId: state?.typeId ?? '',
        failureDate: toMoment(state?.failureDate),
        failureDescription: state?.failureDescription,
        justification: state?.justification,
        notes: state?.notes,
        manualReportId: state?.manualReportId,
        reports: toLabeledReports(
          state?.ejToReports?.reduce(
            (
              options,
              {
                concreteTest,
                concretePlacement,
                lot,
                dailyInspectionControlPoint,
                dailyInspectionInspection,
              }
            ) => {
              const report =
                concreteTest ??
                concretePlacement ??
                lot ??
                dailyInspectionControlPoint ??
                dailyInspectionInspection
              if (report) options.push(report)
              return options
            },
            Array<EJReportOption>()
          )
        ),
      }}
      affectedItems={[
        {
          label: 'Issued By',
          value: state?.issuedBy?.displayName ?? state?.issuedBy?.mail,
        },
        {
          label: 'Issued Date',
          value: dateFormatter.short(state?.issuedDate),
        },
      ]}
      onOk={async ({
        failureDate,
        reports,
        ...rest
      }: CommonEJFormItemsValues) => {
        if (!state?.id)
          return errorHandler('Engineering judgement id must be defined')

        setLoading(true)
        await revert({
          variables: {
            input: {
              id: state.id,
              // Failure date is a required field, so using bang
              failureDate: failureDate!.toISOString(),
              reports: fromLabeledReports(reports),
              ...rest,
            },
          },
        })
        setLoading(false)
        refetch()
        toggle(null)
      }}
    />
  )
}

export default RevertEJModal
